import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import LegalBackToLogin from "../components/LegalBackToLogin";
import { ThemeContext } from "../context/ThemeContext";

const makeStyles = (themeColors: any): Record<string, React.CSSProperties> => ({
  page: {
    minHeight: "100vh",
    background: themeColors.bg,
    color: themeColors.text,
    padding: "48px 18px",
    fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    maxWidth: 980,
    margin: "0 auto",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    borderRadius: 28,
    boxShadow: themeColors.glow,
    padding: "34px clamp(20px, 4vw, 48px)",
    backdropFilter: "blur(18px)",
  },
  eyebrow: {
    color: themeColors.accent2,
    letterSpacing: 2,
    textTransform: "uppercase",
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 10,
  },
  title: {
    fontSize: "clamp(32px, 5vw, 52px)",
    lineHeight: 1.02,
    margin: "0 0 10px",
    color: themeColors.text,
  },
  date: {
    color: themeColors.mutedText,
    marginBottom: 28,
    fontSize: 15,
  },
  section: {
    marginTop: 28,
    paddingTop: 22,
    borderTop: `1px solid ${themeColors.border}`,
  },
  h2: {
    color: themeColors.accent,
    fontSize: 22,
    margin: "0 0 10px",
  },
  p: {
    color: themeColors.text,
    fontSize: 16,
    lineHeight: 1.75,
    margin: "8px 0",
  },
  list: {
    color: themeColors.text,
    lineHeight: 1.75,
    paddingLeft: 22,
  },
  nav: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 36,
    paddingTop: 24,
    borderTop: `1px solid ${themeColors.border}`,
  },
  link: {
    color: themeColors.accent2,
    textDecoration: "none",
    fontWeight: 800,
  },
  footer: {
    marginTop: 22,
    color: themeColors.mutedText,
    fontSize: 13,
    lineHeight: 1.6,
  },
});

export default function AIDisclaimer() {
  const { themeColors } = useContext(ThemeContext);
  const styles = useMemo(() => makeStyles(themeColors), [themeColors]);

  return (
    <main style={styles.page}>
      <article style={styles.card}>
        <div style={styles.eyebrow}>AVAAIO Legal</div>
        <h1 style={styles.title}>AI Disclaimer</h1>
        <div style={styles.date}>
          Effective and Last Updated: July 13, 2026
        </div>

        <section style={styles.section}>
          <h2 style={styles.h2}>AI System Notice</h2>
          <p style={styles.p}>
            Ava is an artificial intelligence system within AVAAIO™.
            Ava is not human and does not possess consciousness,
            self-awareness, emotions, beliefs, personal intent,
            independent moral judgment, or licensed professional
            judgment.
          </p>
          <p style={styles.p}>
            Ava’s words, voice, expressions, eye movement, gestures,
            conversational warmth, and apparent emotional reactions are
            generated by software and should not be interpreted as
            evidence of human feelings or consciousness.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Accuracy and Hallucinations</h2>
          <p style={styles.p}>
            AI may generate information that is inaccurate,
            fabricated, incomplete, outdated, biased, contradictory,
            misleading, or unsuitable for the user’s circumstances.
            AI may sound confident even when an answer is wrong.
          </p>
          <p style={styles.p}>
            Users are responsible for verifying important facts with
            reliable sources and qualified professionals before acting
            on AI-generated information.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Ava Vision Limitations</h2>
          <p style={styles.p}>
            Ava Vision analyzes camera images using computer-vision
            systems. It may miss, misclassify, invent, confuse, or
            incorrectly describe people, objects, text, clothing,
            colors, distances, gestures, hazards, scenes, and events.
          </p>
          <p style={styles.p}>
            Ava Vision does not identify a person, verify identity,
            authenticate an account, establish that a particular person
            is present, diagnose a condition, determine intent, or
            reliably evaluate danger.
          </p>
          <p style={styles.p}>
            Ava Vision must not be relied upon for surveillance,
            security monitoring, emergency detection, childcare,
            eldercare, workplace safety, driving, navigation, medical
            assessment, law enforcement, or any decision affecting a
            person’s rights or safety.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Voice and Transcription Limitations</h2>
          <p style={styles.p}>
            Speech recognition may misunderstand words, names,
            accents, background speech, numbers, commands, or the
            intended speaker. A transcript may differ materially from
            what was said.
          </p>
          <p style={styles.p}>
            Users should review a transcript before relying on it,
            especially when the message concerns money, medication,
            addresses, dates, legal obligations, safety instructions,
            or other important information.
          </p>
          <p style={styles.p}>
            Ava’s spoken voice is synthetic. Tone, pacing, emphasis,
            and emotional style are generated and may not accurately
            represent the meaning, urgency, certainty, or sensitivity
            of the underlying text.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Device Motion Limitations</h2>
          <p style={styles.p}>
            Device Motion allows Ava’s visual presentation to respond
            to device orientation. It does not mean Ava understands the
            physical environment or knows where the device is located.
          </p>
          <p style={styles.p}>
            Motion responses may be delayed, reversed, exaggerated,
            unavailable, or inaccurate because of device hardware,
            browser support, screen orientation, sensor calibration,
            permissions, or software limitations.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Memory Limitations</h2>
          <p style={styles.p}>
            Ava may save or retrieve user-provided information when
            memory features are enabled or requested. Memory can be
            incomplete, outdated, duplicated, incorrectly associated,
            unavailable, or recalled in the wrong context.
          </p>
          <p style={styles.p}>
            Users should correct inaccurate information, review what
            Ava claims to remember, protect any memory code, and avoid
            treating Ava as the only record of important information.
            AVAAIO memory is not a substitute for secure document
            storage, professional recordkeeping, backups, or a password
            manager.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>No Professional Advice</h2>
          <p style={styles.p}>
            AVAAIO and its AI systems do not provide medical, mental-health,
            legal, financial, tax, insurance, employment, engineering,
            emergency, law-enforcement, security, or other licensed
            professional advice.
          </p>
          <p style={styles.p}>
            AI output must not be used as the sole basis for diagnosis,
            treatment, medication decisions, legal filings, financial
            transactions, investments, credit decisions, employment
            decisions, insurance decisions, physical repairs, or
            other high-impact actions.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Emotional and Conversational Responses</h2>
          <p style={styles.p}>
            Ava may provide friendly, empathetic, affectionate, or
            emotionally supportive language. This behavior is
            algorithmic and does not establish a human relationship,
            therapeutic relationship, confidential professional
            relationship, duty of care, or guarantee that Ava
            understands the user’s emotional condition.
          </p>
          <p style={styles.p}>
            Ava is not a substitute for family, trusted people, a
            licensed mental-health professional, crisis counselor, or
            emergency service.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Online, Offline, and Provider Limitations</h2>
          <p style={styles.p}>
            Some AVAAIO functions depend on internet access and
            third-party AI, speech, authentication, billing, email, or
            cloud providers. Other functions may use local or offline
            components with more limited capabilities.
          </p>
          <p style={styles.p}>
            Switching providers or entering an offline mode may change
            accuracy, available knowledge, memory access, response
            quality, voice behavior, or feature availability. A local
            response is not necessarily more accurate than an online
            response.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>User Content and Privacy</h2>
          <p style={styles.p}>
            Users are responsible for the prompts, images, audio,
            files, personal information, and other content they submit.
            Do not provide confidential, privileged, regulated,
            financial, medical, biometric, or security-sensitive
            information unless it is necessary and the user understands
            the associated risks.
          </p>
          <p style={styles.p}>
            Users must obtain any consent required before capturing or
            processing another person’s voice, image, personal
            information, or activity.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Emergency Use</h2>
          <p style={styles.p}>
            AVAAIO and its AI systems must not be used to detect, manage, or
            respond to an emergency. AI may fail to recognize danger,
            misunderstand a request, provide a delayed response, or be
            unavailable.
          </p>
          <p style={styles.p}>
            In an emergency or immediate danger, contact local
            emergency services or an appropriate qualified person
            immediately.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Final Responsibility</h2>
          <p style={styles.p}>
            The user remains responsible for decisions, actions,
            communications, purchases, recordings, submitted content,
            and consequences arising from use of AVAAIO. Important
            output should always receive appropriate human review.
          </p>
        </section>

          <LegalBackToLogin />
        <nav style={styles.nav} aria-label="Legal navigation">
          <Link style={styles.link} to="/privacy-policy">
            Privacy Policy
          </Link>
          <Link style={styles.link} to="/terms-and-conditions">
            Terms & Conditions
          </Link>
          <Link style={styles.link} to="/ai-disclaimer">
            AI Disclaimer
          </Link>
          <Link style={styles.link} to="/credits">
            Credits & Acknowledgments
          </Link>
        </nav>

        <footer style={styles.footer}>
          © 2026 American Globalo, LLC. All rights reserved.
          <br />
          AVAAIO™ is owned and operated by American Globalo, LLC.
          Ava is the AI assistant experience within the AVAAIO™
          platform.
          <br />
          Support: support@avaaio.com
        </footer>
      </article>
    </main>
  );
}
